import { Link } from "react-router-dom";
import { ArrowRight, Building2, Landmark, Hammer } from "lucide-react";

const Bullet = ({ children }) => (
  <li className="flex items-start gap-2.5 text-sm text-current/80 sm:text-base">
    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-current opacity-60" />
    <span className="opacity-90">{children}</span>
  </li>
);

export const PathwaySummaries = () => (
  <>
    {/* ─── Clinq for Business ─── */}
    <section id="for-business" data-testid="summary-for-business" className="bg-white text-neutral-900">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-6 py-20 lg:grid-cols-[1.2fr_1fr] lg:py-28">
        <div>
          <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#0A8B85]">
            <Building2 className="h-4 w-4" /> Clinq for Business
          </p>
          <h2 className="mt-4 text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl" style={{ fontFamily: "Manrope, sans-serif" }}>
            Get funder-ready.<br />
            <span className="text-[#0A8B85]">Get funded.</span>
          </h2>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-neutral-600 sm:text-lg">
            Most founders spend years chasing funding without knowing why they keep getting
            turned down. Clinq changes that.
          </p>
          <p className="mt-4 max-w-xl text-base leading-relaxed text-neutral-600 sm:text-lg">
            We score your business using a proprietary Capital Readiness methodology built on
            the mandates of 1,000+ investors and funders. We know exactly what funders look
            for — and we will tell you who is most likely to fund your business, how much you
            can realistically raise, and what you need to do to get there.
          </p>
          <p className="mt-5 text-sm font-bold uppercase tracking-wider text-neutral-800" data-testid="summary-business-report-shows">
            Your Capital Readiness Report shows you:
          </p>
          <ul className="mt-3 space-y-2.5 text-neutral-700">
            <Bullet>Your Capital Readiness Score out of 100</Bullet>
            <Bullet>Which funders in our network are the right fit for your business</Bullet>
            <Bullet>The types of funders best suited to your industry, stage, and business model</Bullet>
            <Bullet>How much capital your business could potentially raise</Bullet>
            <Bullet>The likelihood of each funder saying yes</Bullet>
            <Bullet>If you are not ready yet — exactly what to fix to increase your chances</Bullet>
            <Bullet>Whether your documentation is in a ready state for Clinq to facilitate a funder introduction</Bullet>
          </ul>
          <p className="mt-5 max-w-xl text-sm font-semibold text-[#0A8B85]" data-testid="summary-business-connected-note">
            Businesses with strong scores are actively connected by Clinq to funders in our network.
          </p>
          <div data-testid="summary-business-docs-note"
            className="mt-6 max-w-xl rounded-xl border border-[#0A8B85]/25 bg-[#F4F8F8] px-4 py-3 text-sm text-neutral-700">
            Don&rsquo;t have all the right funder documentation?{" "}
            <span className="font-bold text-[#0A8B85]">Don&rsquo;t worry — Clinq will help you build it.</span>
          </div>
          <Link
            to="/for-business"
            data-testid="summary-business-read-more"
            className="group mt-8 inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#0A8B85] px-7 py-3.5 text-sm font-bold text-white shadow-lg shadow-[#0A8B85]/25 transition-all hover:bg-[#066667] sm:w-auto"
          >
            Read More
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </div>
        <div className="hidden lg:block">
          <div className="rounded-2xl border border-neutral-200 bg-[#F4F8F8] p-8 shadow-[0_30px_60px_-25px_rgba(10,139,133,0.35)]">
            <div className="flex items-center justify-between text-[11px] font-bold uppercase tracking-[0.16em] text-neutral-500">
              <span>Capital Readiness</span>
              <span className="text-[#0A8B85]">Sample</span>
            </div>
            <div className="mt-5 flex items-end gap-3">
              <span className="text-6xl font-extrabold text-[#0A8B85]" style={{ fontFamily: "Manrope, sans-serif" }}>82</span>
              <span className="pb-2 text-sm font-semibold text-neutral-400">/100 · Funder-Ready</span>
            </div>
            <div className="mt-4 h-2 overflow-hidden rounded-full bg-neutral-200">
              <div className="h-full w-[82%] rounded-full bg-[#0A8B85]" />
            </div>
            <p className="mt-2 text-[11px] font-bold uppercase tracking-wider text-[#D1A330]">
              Target: 70+ (Market Ready) to attract funders
            </p>
            <div className="mt-6 space-y-2.5">
              {["Pitch deck", "Financial model", "Management accounts"].map((d) => (
                <div key={d} className="flex items-center justify-between rounded-lg bg-white px-4 py-2.5 text-sm font-medium text-neutral-700">
                  {d}
                  <span className="text-xs font-bold text-[#0A8B85]">✓</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ─── Clinq for Projects ─── */}
    <section id="for-projects" data-testid="summary-for-projects" className="border-y border-[#E8DFC5] bg-[#FBF7EC] text-neutral-900">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-6 py-20 lg:grid-cols-[1fr_1.2fr] lg:py-28">
        <div className="order-2 hidden lg:order-1 lg:block">
          <div className="rounded-2xl border border-[#E8DFC5] bg-white p-8 shadow-[0_30px_60px_-25px_rgba(209,163,48,0.35)]">
            <div className="flex items-center justify-between text-[11px] font-bold uppercase tracking-[0.16em] text-neutral-500">
              <span>Project Readiness</span>
              <span className="text-[#D1A330]">Sample</span>
            </div>
            <p className="mt-4 text-lg font-extrabold text-neutral-900">Nakuru Solar Park</p>
            <p className="text-xs font-semibold text-neutral-500">Renewable energy · KES 850M total cost</p>
            <div className="mt-5 flex items-end gap-3">
              <span className="text-5xl font-extrabold text-[#D1A330]" style={{ fontFamily: "Manrope, sans-serif" }}>72</span>
              <span className="pb-1.5 text-sm font-semibold text-neutral-400">/100 · Bankable</span>
            </div>
            <div className="mt-3 h-2 overflow-hidden rounded-full bg-neutral-200">
              <div className="h-full w-[72%] rounded-full bg-[#D1A330]" />
            </div>
            <div className="mt-5 space-y-2.5">
              {["Feasibility study", "Financial model", "Permits & land"].map((d) => (
                <div key={d} className="flex items-center justify-between rounded-lg bg-[#FBF7EC] px-4 py-2.5 text-sm font-medium text-neutral-700">
                  {d}
                  <span className="text-xs font-bold text-[#D1A330]">✓</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="order-1 lg:order-2">
          <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#D1A330]">
            <Hammer className="h-4 w-4" /> Clinq for Projects
          </p>
          <h2 className="mt-4 text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl" style={{ fontFamily: "Manrope, sans-serif" }}>
            Funding a project?<br />
            <span className="text-[#D1A330]">Prove it's bankable.</span>
          </h2>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-neutral-600 sm:text-lg">
            Solar parks, real estate developments, agri-processing, infrastructure — Clinq
            scores your project the way financiers assess it, then matches you with DFIs,
            infrastructure funds, banks and private credit built to fund it.
          </p>
          <ul className="mt-6 space-y-2.5 text-neutral-700">
            <Bullet>A Project Readiness Score that shows exactly how bankable you look</Bullet>
            <Bullet>Evidence-backed — feasibility, financial model, permits and offtakes</Bullet>
            <Bullet>Matched with project financiers — and we can make the approach for you</Bullet>
          </ul>
          <Link
            to="/for-projects"
            data-testid="summary-projects-read-more"
            className="group mt-8 inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#D1A330] px-7 py-3.5 text-sm font-bold text-white shadow-lg shadow-[#D1A330]/25 transition-all hover:bg-[#B8901F] sm:w-auto"
          >
            Read More
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </div>
      </div>
    </section>

    {/* ─── Clinq for Funders ─── */}
    <section id="for-funders" data-testid="summary-for-funders" className="bg-[#04302E] text-white">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-6 py-20 lg:grid-cols-[1fr_1.2fr] lg:py-28">
        <div className="order-2 hidden lg:order-1 lg:block">
          <div className="rounded-2xl border border-white/10 bg-white/5 p-8 backdrop-blur-sm">
            <div className="flex items-center justify-between text-[11px] font-bold uppercase tracking-[0.16em] text-white/50">
              <span>Deal Flow</span>
              <span className="text-[#ECC764]">Compatibility Score</span>
            </div>
            <div className="mt-5 space-y-3">
              {[
                ["Fintech · Kenya", "85%"],
                ["Real Estate · Nairobi", "81%"],
                ["Agritech · Nigeria", "78%"],
                ["Solar Project · Nakuru", "72%"],
              ].map(([name, score]) => (
                <div key={name} className="flex items-center justify-between rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-sm">
                  <span className="font-medium text-white/85">{name}</span>
                  <span className="rounded-full bg-[#ECC764]/15 px-2.5 py-0.5 text-xs font-bold text-[#ECC764]">{score}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="order-1 lg:order-2">
          <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#ECC764]">
            <Landmark className="h-4 w-4" /> Clinq for Funders
          </p>
          <h2 className="mt-4 text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl" style={{ fontFamily: "Manrope, sans-serif" }}>
            Grow your money by finding{" "}
            <span className="text-[#7BD3CE]">funder-ready opportunities</span> that
            perfectly fit your risk level and investment size.
          </h2>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-white/70 sm:text-lg">
            Whether you invest solo, as a chama or investment club, or deploy
            institutional capital — Clinq surfaces scored, document-verified African
            businesses and projects that fit your risk profile.
          </p>
          <ul className="mt-6 space-y-2.5 text-white">
            <Bullet>Every opportunity scored and backed by verified documents</Bullet>
            <Bullet>Set your risk profile once — deal flow filters itself</Bullet>
            <Bullet>Track your pipeline from first look to closed deal</Bullet>
          </ul>
          <Link
            to="/for-funders"
            data-testid="summary-funders-read-more"
            className="group mt-8 inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#ECC764] px-7 py-3.5 text-sm font-bold text-[#04302E] shadow-lg shadow-black/30 transition-all hover:bg-[#D1A330] sm:w-auto"
          >
            Read More
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </div>
      </div>
    </section>
  </>
);
